import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';
import { isStancerOrigin, isStancerPaymentAuthorized } from '../../lib/stancerPayment';

/** `jardins` = fond click & collect / menu (#EAE7E3). */
const OVERLAY_CLASS = {
  dark: 'bg-black/80',
  jardins: 'bg-[#EAE7E3] backdrop-blur-md',
};

export default function StancerPaymentModal({
  iframeUrl,
  paymentId,
  onAuthorized,
  onError,
  onClose,
  overlayVariant = 'dark',
}) {
  const handledRef = useRef(false);
  const onAuthorizedRef = useRef(onAuthorized);
  const onErrorRef = useRef(onError);
  const [mounted, setMounted] = useState(false);

  onAuthorizedRef.current = onAuthorized;
  onErrorRef.current = onError;

  useEffect(() => {
    setMounted(true);
    handledRef.current = false;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [paymentId, iframeUrl]);

  useEffect(() => {
    const onMessage = (event) => {
      if (!isStancerOrigin(event.origin)) return;
      if (handledRef.current) return;

      if (isStancerPaymentAuthorized(event.data)) {
        handledRef.current = true;
        onAuthorizedRef.current(paymentId);
        return;
      }

      const data = event.data;
      if (data && typeof data === 'object') {
        const status = String(data.status ?? '').toLowerCase();
        if (
          status === 'error' ||
          status === 'secure-auth-error' ||
          status === 'invalid' ||
          status === 'failed'
        ) {
          handledRef.current = true;
          onErrorRef.current('Le paiement a échoué. Veuillez réessayer.');
        }
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [paymentId]);

  if (!mounted || !iframeUrl) return null;

  const modal = (
    <div
      className={`fixed inset-0 z-[1300] flex items-center justify-center p-1 sm:p-2 ${
        OVERLAY_CLASS[overlayVariant] || OVERLAY_CLASS.dark
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="stancer-payment-title"
    >
      <div className="w-full max-w-4xl xl:max-w-5xl h-[calc(100dvh-0.5rem)] sm:h-[calc(100dvh-1rem)] rounded-xl sm:rounded-2xl overflow-hidden flex flex-col min-h-0 bg-card border border-border shadow-elevation-3">
        <div className="flex-shrink-0 px-4 py-2.5 sm:px-5 sm:py-3 flex items-center justify-between gap-3 border-b border-border">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Icon name="Lock" size={18} className="text-primary shrink-0" />
            <div className="min-w-0">
              <p id="stancer-payment-title" className="text-sm font-semibold text-foreground">
                Paiement sécurisé
              </p>
            </div>
          </div>
          <button
            type="button"
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted shrink-0"
            aria-label="Fermer le paiement"
            onClick={onClose}
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <iframe
          src={iframeUrl}
          title="Paiement Stancer"
          className="w-full flex-1 min-h-0 border-0 bg-background block"
          allow="payment *; fullscreen"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals"
        />
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
